import { ProductCatalogRecord, useProductCatalogCache } from "api/cached";
import { OptionItem } from "commons/ultilities/dynamic-form/models/control";
import { mapToDistinctOptionItems } from "commons/ultilities/option-item/option-item-ultil";
import { useCallback, useEffect, useMemo } from "react";

export type ProductGroupSelectionKey =
  | "dealingName"
  | "name"
  | "code"
  | "barcode";

export type ProductGroupSelectionModel = {
  dealingName: string;
  name: string;
  code: string;
  barcode: string;
};

const FILTER_KEYS: ProductGroupSelectionKey[] = [
  "dealingName",
  "name",
  "code",
  "barcode",
];

const normalizeValue = (value: string | null | undefined): string => {
  return value?.trim() ?? "";
};

const normalizeText = (value: string | null | undefined): string => {
  return normalizeValue(value).toLowerCase();
};

const filterByKeyword = (
  options: OptionItem[],
  keyword: string,
): OptionItem[] => {
  if (!keyword) return options;
  const normalizedKeyword = normalizeText(keyword);
  return options.filter((x) =>
    normalizeText(x.label).includes(normalizedKeyword),
  );
};

const applySelection = (
  records: ProductGroupSelectionModel[],
  selection: ProductGroupSelectionModel,
  ignoreKey?: ProductGroupSelectionKey,
): ProductGroupSelectionModel[] => {
  return records.filter((record) => {
    return FILTER_KEYS.every((key) => {
      if (key === ignoreKey) return true;
      const selectedValue = selection[key];
      if (!selectedValue) return true;
      return record[key] === selectedValue;
    });
  });
};

const getOptionsByKey = (
  records: ProductGroupSelectionModel[],
  key: ProductGroupSelectionKey,
): OptionItem[] => {
  return mapToDistinctOptionItems(
    records.filter((record) => !!record[key]),
    (record) => record[key],
  )
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((option) => ({
      ...option,
      value: String(option.value),
      label: String(option.label),
    }));
};

export const useProductGroupSelection = (
  selection: ProductGroupSelectionModel,
) => {
  const { data, error } = useProductCatalogCache();

  const records = useMemo<ProductGroupSelectionModel[]>(() => {
    return (
      data?.map(
        (item: ProductCatalogRecord): ProductGroupSelectionModel => ({
          dealingName: normalizeValue(item.dealingName),
          name: normalizeValue(item.name),
          code: normalizeValue(item.code),
          barcode: normalizeValue(item.barcode),
        }),
      ) ?? []
    );
  }, [data]);

  useEffect(() => {
    if (!error) return;
    console.error("Fail load order product filter options", error);
  }, [error]);

  const searchOptions = useCallback(
    async (key: ProductGroupSelectionKey, keyword: string) => {
      const filteredRecords = applySelection(records, selection, key);
      const options = getOptionsByKey(filteredRecords, key);
      return filterByKeyword(options, keyword);
    },
    [records, selection],
  );

  const searchDealingNameOptions = useCallback(
    async (keyword: string) => searchOptions("dealingName", keyword),
    [searchOptions],
  );

  const searchNameOptions = useCallback(
    async (keyword: string) => searchOptions("name", keyword),
    [searchOptions],
  );

  const searchCodeOptions = useCallback(
    async (keyword: string) => searchOptions("code", keyword),
    [searchOptions],
  );

  const searchBarcodeOptions = useCallback(
    async (keyword: string) => searchOptions("barcode", keyword),
    [searchOptions],
  );

  const getValidSelectionAfterChange = useCallback(
    (
      keyChanged: ProductGroupSelectionKey,
      valueChanged: string,
    ): ProductGroupSelectionModel => {
      const nextSelection: ProductGroupSelectionModel = {
        ...selection,
        [keyChanged]: valueChanged,
      };

      for (const key of FILTER_KEYS) {
        if (key === keyChanged || !nextSelection[key]) continue;

        const filteredRecords = applySelection(records, nextSelection, key);
        const optionValues = new Set(
          getOptionsByKey(filteredRecords, key).map((x) => x.value),
        );

        if (!optionValues.has(nextSelection[key])) {
          nextSelection[key] = "";
        }
      }

      return nextSelection;
    },
    [records, selection],
  );

  return useMemo(
    () => ({
      searchDealingNameOptions,
      searchNameOptions,
      searchCodeOptions,
      searchBarcodeOptions,
      getValidSelectionAfterChange,
    }),
    [
      getValidSelectionAfterChange,
      searchBarcodeOptions,
      searchCodeOptions,
      searchDealingNameOptions,
      searchNameOptions,
    ],
  );
};
